import {
  Box,
  Breadcrumbs,
  Button,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Org } from "~/lib/types";
import { Loading, LoadingFailedPage } from "~/components/loading";
import { useLoadOrganizationStatsQuery } from "~/operations";
import { Home as HomeIcon } from "@mui/icons-material";
import { CanCreateSpace } from "~/lib/iam/actions/mondoo/captain/Captain/CreateSpace";
import { AddButton } from "~/components/add-button";
import { FilterBar } from "~/components/FilterBar/FilterBar";
import { useSearch } from "~/components/search/useSearch";
import { EmptyState } from "~/components/empty-state/empty-state";
import { Metrics } from "~/lib/arrow";
import { setDocumentTitle } from "~/lib/commonUtils";
import { ViewSwitcher, ViewType } from "~/components/view-switcher";
import { SpaceSummaryList } from "~/components/SpaceSummary/SpaceSummaryList";
import { getDailyCountsTimeline } from "~/components/PriorityFindings/useDailyCounts";

export type OrganizationSpacesPageProps = {
  org: Org;
};

export function OrganizationSpacesPage({ org }: OrganizationSpacesPageProps) {
  const search = useSearch();

  const { startTime, endTime } = useMemo(() => getDailyCountsTimeline(28), []);

  const { data, loading, error, fetchMore } = useLoadOrganizationStatsQuery({
    variables: {
      mrn: org.mrn,
      query: search.searchFilters.join(" "),
      metricMrns: [
        Metrics.AssetsRiskRatingDistribution,
        Metrics.HistoricalDailyAssetCount,
        Metrics.HistoricalDailyFindingCount,
      ],
      startTime,
      endTime,
    },
    fetchPolicy: "cache-and-network",
  });

  const {
    edges = [],
    totalCount = 0,
    pageInfo,
  } = data?.organization?.spacesList || {};

  const spaceSummaries = edges.map(({ node }) => {
    const metricsEntries =
      node.metrics?.__typename === "MetricsResponse"
        ? (node.metrics.entries ?? undefined)
        : undefined;
    return {
      spaceId: node.id,
      ...node,
      metricsEntries,
    };
  });

  const views = [ViewType.Cards, ViewType.List];

  if (!loading && (error || !data?.organization)) {
    return <LoadingFailedPage what="organization" />;
  }

  const breadcrumbs = [
    <Link
      key="/organization/overview"
      component={RouterLink}
      to={`/organization/overview?organizationId=${org.id}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Typography key="/organization/spaces">Spaces</Typography>,
  ];

  setDocumentTitle(["Spaces", org.name]);

  const hasSpace = org.spacesCount !== 0;
  const noSpaceExistOnFilter =
    edges.length === 0 && Boolean(search.searchFilters.length);

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3, overflowWrap: "anywhere" }} separator="›">
        {breadcrumbs}
      </Breadcrumbs>
      {/* Heading */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
        <Grid size={12}>
          <Typography
            variant="h4"
            component="h1"
            fontWeight={700}
            textTransform="uppercase"
          >
            Spaces
          </Typography>
        </Grid>
        <CanCreateSpace>
          <Grid size={12}>
            <AddButton
              id="space"
              href={`/organization/spaces/new?organizationId=${org.id}`}
            />
          </Grid>
        </CanCreateSpace>
      </Box>
      {hasSpace && (
        <Grid container sx={{ mb: 3 }}>
          <Grid container size={12} gap={3} alignItems="flex-start">
            <Grid size="grow">
              <FilterBar
                searchId="org-spaces-search"
                placeholder="organization_spaces"
                search={search}
                mb={0}
              />
            </Grid>
            <Grid size="auto" sx={{ pt: 0.37 }}>
              <ViewSwitcher views={views} defaultView={views[0]} />
            </Grid>
          </Grid>
        </Grid>
      )}
      {!hasSpace && (
        <Grid size={{ xs: 12 }}>
          <EmptyState contentType="organization-spaces" />
        </Grid>
      )}
      {noSpaceExistOnFilter && (
        <Grid size={{ xs: 12 }}>
          <EmptyState contentType="organization-spaces-filtered">
            <Button
              onClick={() => search.handleClearSearch()}
              variant="outlined"
              sx={{
                mt: 3,
                borderColor: "secondary.light",
                color: "secondary.light",
              }}
            >
              Clear All Filters
            </Button>
          </EmptyState>
        </Grid>
      )}
      {loading && <Loading what="spaces" />}
      {!loading && totalCount > 0 && (
        <SpaceSummaryList
          className="spaces"
          views={views}
          spaceSummaries={spaceSummaries}
          PaginationProps={{
            fetchMore,
            pageInfo,
            totalCount,
          }}
        />
      )}
    </Box>
  );
}
